import { checkNever } from "@/utils/checkNever";
import oMap from "@/utils/oMap";

//

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type constructor<T> = new (...args: any[]) => T;

export interface RegisterProps {
  useClass: <T>(c: constructor<T>) => { value: constructor<T>; type: "class" };
  useValue: <T>(value: T) => { value: T; type: "value" };
}

export type Register =
  | { value: constructor<unknown>; type: "class" }
  | { value: unknown; type: "value" };

export type ExtractValue<Registers extends Record<string, Register>, P extends keyof Registers> =
  Registers[P]["value"] extends constructor<infer U> ? U : Registers[P]["value"];

type InjectDecorator = (
  target: unknown,
  propertyKey: string | symbol | undefined,
  parameterIndex: number,
) => void;

//

export abstract class IDi<Registers extends Record<string, Register>> {
  public keys = {} as { [P in keyof Registers]: P };

  //

  constructor(registers: (props: RegisterProps) => Registers) {
    this.registerAll(registers);
  }

  //

  public abstract resolve_core<K extends keyof Registers & string>(
    token: K,
  ): ExtractValue<Registers, K>;

  //

  protected abstract register<T>(token: string, provider: constructor<T>): string;

  //

  protected abstract registerValue(token: string, value: unknown): string;

  //

  public resolve<K extends keyof Registers & string>(
    token: (keys: { [P in keyof Registers]: P }) => K,
  ): ExtractValue<Registers, K> {
    return this.resolve_core(token(this.keys));
  }

  protected registerAll(registers: (props: RegisterProps) => Registers) {
    const values = registers({
      useClass: (value) => ({ value, type: "class" }),
      useValue: (value) => ({ value, type: "value" }),
    });

    oMap(values, ({ key: _key, value }) => {
      this.keys[_key] = _key;
      const key = _key.toString();

      switch (value.type) {
        case "class":
          this.register(key, value.value);
          break;
        case "value":
          this.registerValue(key, value.value);
          break;
        default:
          checkNever(value);
          throw new Error(`Неизвестный тип регистрации для токена: ${key}.`);
      }
    });
  }
}

//

export interface IDiDecorator<Tokens extends string> {
  inject(token: Tokens): InjectDecorator;
  injectable<T>(): (target: constructor<T>) => void;
  singleton<T>(): (target: constructor<T>) => void;
}
